// Lucka 6: TA-plan som spårbart dokument med giltighetstid, ansvarig och etapp.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { Cone, Plus, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { generateTrafficPlanWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Plan {
  id: string;
  title: string;
  area: string | null;
  responsible_name: string | null;
  responsible_email: string | null;
  authority: string | null;
  permit_ref: string | null;
  valid_from: string | null;
  valid_to: string | null;
  measures: string | null;
  status: string;
  stage_id: string | null;
}

interface Stage {
  id: string;
  name: string;
}

const fmt = (v: string | null) => (v ? new Date(v).toLocaleDateString("sv-SE") : "–");

const daysLeft = (v: string | null) =>
  v ? Math.ceil((new Date(v).getTime() - Date.now()) / 86400000) : null;

const TrafficPlans: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Plan[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data, error }, { data: stageData }] = await Promise.all([
      supabase
        .from("traffic_plans")
        .select(
          "id, title, area, responsible_name, responsible_email, authority, permit_ref, valid_from, valid_to, measures, status, stage_id"
        )
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
      supabase.from("project_stages").select("id, name").eq("project_id", projectId).order("sort_order"),
    ]);
    if (error) toast.error("Kunde inte läsa TA-planerna");
    setRows((data as Plan[] | null) ?? []);
    setStages((stageData as Stage[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("traffic_plans").insert({
      project_id: projectId,
      title: "Ny trafikanordningsplan",
      created_by: auth?.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte skapa TA-planen");
    load();
  };

  const save = async (id: string, patch: Partial<Plan>) => {
    const { error } = await supabase.from("traffic_plans").update(patch as never).eq("id", id);
    if (error) return toast.error("Kunde inte spara");
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("traffic_plans").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const exportWord = async () => {
    try {
      await generateTrafficPlanWordDocument(
        projectId,
        rows.map((r) => ({ ...r, stageName: stages.find((s) => s.id === r.stage_id)?.name ?? null }))
      );
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="07b"
        title="TA-plan – trafikanordning"
        description="Avstängningar, gångvägar och omledningar med giltighetstid, ansvarig och tillstånd från väghållaren."
        icon={Cone}
        projectId={projectId}
        exportHistory={{ documentType: "traffic_plan" }}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={exportWord}>
              <FileDown className="mr-2 h-4 w-4" /> Exportera Dok 07b
            </Button>
            <Button onClick={add}>
              <Plus className="mr-2 h-4 w-4" /> Ny TA-plan
            </Button>
          </div>
        }
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga TA-planer registrerade. Lägg till en om arbetet påverkar gator, gång- eller cykelvägar.
          </CardContent>
        </Card>
      ) : (
        rows.map((p) => {
          const left = daysLeft(p.valid_to);
          return (
            <Card key={p.id}>
              <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
                <div className="space-y-1">
                  <CardTitle className="text-base">{p.title}</CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={p.status === "approved" ? "default" : "secondary"}>
                      {p.status === "approved" ? "Godkänd" : "Utkast"}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {fmt(p.valid_from)} – {fmt(p.valid_to)}
                    </span>
                    {left !== null && left < 0 && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" /> Utgången
                      </Badge>
                    )}
                    {left !== null && left >= 0 && left <= 14 && (
                      <Badge variant="secondary">Går ut om {left} dagar</Badge>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="icon" aria-label="Ta bort TA-plan" onClick={() => remove(p.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <Label>Benämning</Label>
                    <Input defaultValue={p.title} onBlur={(e) => save(p.id, { title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Område</Label>
                    <Input
                      defaultValue={p.area ?? ""}
                      onBlur={(e) => save(p.id, { area: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Ansvarig</Label>
                    <Input
                      defaultValue={p.responsible_name ?? ""}
                      onBlur={(e) => save(p.id, { responsible_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>E-post till ansvarig</Label>
                    <Input
                      type="email"
                      defaultValue={p.responsible_email ?? ""}
                      onBlur={(e) => save(p.id, { responsible_email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Väghållare eller myndighet</Label>
                    <Input
                      defaultValue={p.authority ?? ""}
                      onBlur={(e) => save(p.id, { authority: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Tillståndsnummer</Label>
                    <Input
                      defaultValue={p.permit_ref ?? ""}
                      onBlur={(e) => save(p.id, { permit_ref: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Giltig från</Label>
                    <Input
                      type="date"
                      defaultValue={p.valid_from ?? ""}
                      onBlur={(e) => save(p.id, { valid_from: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Giltig till</Label>
                    <Input
                      type="date"
                      defaultValue={p.valid_to ?? ""}
                      onBlur={(e) => save(p.id, { valid_to: e.target.value || null })}
                    />
                  </div>
                  {stages.length > 0 && (
                    <div className="space-y-1">
                      <Label>Etapp</Label>
                      <Select
                        value={p.stage_id ?? "none"}
                        onValueChange={(v) => save(p.id, { stage_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Välj etapp" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Hela projektet</SelectItem>
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <Label>Åtgärder och skyltning</Label>
                  <Textarea
                    defaultValue={p.measures ?? ""}
                    onBlur={(e) => save(p.id, { measures: e.target.value })}
                    placeholder="Avstängning, tillfällig gångbana, hastighetsbegränsning, belysning…"
                  />
                </div>

                <Button
                  variant={p.status === "approved" ? "outline" : "secondary"}
                  onClick={() => save(p.id, { status: p.status === "approved" ? "draft" : "approved" })}
                >
                  {p.status === "approved" ? "Återta godkännande" : "Markera som godkänd"}
                </Button>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default TrafficPlans;
